import type { ApiClientName } from '@/stores/diagnosticsStore'

/** UI labels for axios client names. Used by client filter tabs + Client column cell. */
export const CLIENT_LABELS: Record<ApiClientName, string> = {
	api: 'Project',
	owner: 'Org',
	console: 'Console',
}

/**
 * Frontend percentile calculations for API latency metrics.
 * Input: array of durations (ms). Output: { p50, p95, p99 }.
 */
export interface PercentileResult {
	p50: number
	p95: number
	p99: number
}

export const computePercentiles = (values: number[]): PercentileResult => {
	if (values.length === 0) {
		return { p50: 0, p95: 0, p99: 0 }
	}
	const sorted = [...values].sort((a, b) => a - b)
	const pick = (p: number): number => {
		const idx = Math.min(sorted.length - 1, Math.max(0, Math.ceil((p / 100) * sorted.length) - 1))
		return sorted[idx]
	}
	return { p50: pick(50), p95: pick(95), p99: pick(99) }
}

export const computeAverage = (values: number[]): number => {
	if (values.length === 0) {
		return 0
	}
	let sum = 0
	for (const v of values) {
		sum += v
	}
	return sum / values.length
}

/**
 * Look up a PerformanceResourceTiming entry by URL pathname + nearest startTime.
 * Matching by full href fails for requests with serialized params or when the
 * browser canonicalizes the URL differently, so we match on pathname and pick
 * the entry whose start time is closest to the axios request start.
 *
 * Note: same-origin entries expose full timing; cross-origin entries require
 * `Timing-Allow-Origin` header to return non-zero values for connect/secure
 * fields.
 */
export interface ResourceTimingBreakdown {
	ttfb?: number
	download?: number
	// No server processing has happened yet at these points, so they are the only pure network
	// figures available. Both are absent once a connection is reused, which is most requests.
	dns?: number
	connect?: number
	transferBytes?: number
}

const MAX_TIME_DELTA_MS = 1000

const extractPathname = (url: string): string => {
	try {
		return new URL(url, window.location.origin).pathname
	} catch {
		return url.split('?')[0]
	}
}

export const findResourceTiming = (url: string, startTime: number): ResourceTimingBreakdown | undefined => {
	if (typeof performance === 'undefined' || typeof performance.getEntriesByType !== 'function') {
		return undefined
	}
	try {
		const entries = performance.getEntriesByType('resource') as PerformanceResourceTiming[]
		const targetPath = extractPathname(url)

		let best: PerformanceResourceTiming | undefined
		let bestDelta = Number.POSITIVE_INFINITY

		for (let i = entries.length - 1; i >= 0; i--) {
			const entry = entries[i]
			if (extractPathname(entry.name) !== targetPath) {
				continue
			}
			const entryStart = performance.timeOrigin + entry.startTime
			const delta = Math.abs(entryStart - startTime)
			if (delta < bestDelta) {
				best = entry
				bestDelta = delta
			}
		}

		if (!best || bestDelta > MAX_TIME_DELTA_MS) {
			return undefined
		}

		const reqStart = best.requestStart
		const resStart = best.responseStart
		const resEnd = best.responseEnd

		const ttfb = Math.max(0, resStart - reqStart)
		const download = Math.max(0, resEnd - resStart)
		// Zero means the phase did not happen, not that it was instant; report absent.
		const dns = Math.max(0, best.domainLookupEnd - best.domainLookupStart)
		const connect = Math.max(0, best.connectEnd - best.connectStart)
		const transferBytes = best.encodedBodySize

		return {
			ttfb: ttfb >= 0 ? ttfb : undefined,
			download: download >= 0 ? download : undefined,
			dns: dns > 0 ? dns : undefined,
			connect: connect > 0 ? connect : undefined,
			transferBytes: transferBytes > 0 ? transferBytes : undefined,
		}
	} catch {
		return undefined
	}
}

export const formatMs = (ms: number): string => {
	if (ms < 1) {
		return '<1 ms'
	}
	if (ms >= 1000) {
		return `${(ms / 1000).toFixed(2)} s`
	}
	return `${Math.round(ms)} ms`
}

export interface ConnectionSetup {
	connect: number
	dns?: number
	/** Milliseconds since the page opened this connection. */
	ageMs: number
}

/**
 * Connection setup for the origin, read from the navigation entry.
 *
 * API requests cannot supply this: the connection is opened during initial page load, so by the
 * time any of them run it is being reused and their entries report a zero setup phase. The
 * navigation entry is the one place the handshake is recorded, and it is the same connection the
 * API calls go on to reuse.
 */
export const findConnectionSetup = (now: number): ConnectionSetup | undefined => {
	if (typeof performance === 'undefined' || typeof performance.getEntriesByType !== 'function') {
		return undefined
	}
	try {
		const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined
		if (!nav) {
			return undefined
		}
		const connect = Math.max(0, nav.connectEnd - nav.connectStart)
		if (connect <= 0) {
			return undefined
		}
		const dns = Math.max(0, nav.domainLookupEnd - nav.domainLookupStart)
		return {
			connect,
			dns: dns > 0 ? dns : undefined,
			ageMs: Math.max(0, now - (performance.timeOrigin + nav.startTime)),
		}
	} catch {
		return undefined
	}
}
